/**
 * FOS Decision Panel
 *
 * A reusable, elegant panel for displaying FOS decision details.
 * Can be used as a sheet (slide-in panel) or embedded inline.
 *
 * Features:
 * - Clean, narrow design optimized for quick scanning
 * - Inline PDF viewer toggle
 * - Semantic outcome colors via OutcomeBadge
 * - Compact information hierarchy
 *
 * Usage:
 * - FOS Explorer results detail view
 * - Signal detail "Similar Decisions"
 * - Anywhere FOS decisions are referenced
 */

import React, { useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';
import { useFOSDecision } from '../../../hooks/useFOSExplorer';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../ui/sheet';
import { Button } from '../../ui/button';
import { Skeleton } from '../../ui/skeleton';
import { ScrollArea } from '../../ui/scroll-area';
import { OutcomeBadge, normalizeOutcome } from '../../ui/outcome-badge';
import { Separator } from '../../ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import IconWrapper from '../../IconWrapper';
import type { IconType } from 'react-icons';
import {
  FiExternalLink,
  FiBriefcase,
  FiFileText,
  FiCalendar,
  FiAlertTriangle,
  FiChevronRight,
  FiDollarSign,
  FiUser,
  FiMessageSquare,
  FiFile,
} from 'react-icons/fi';
import type { FOSDecisionDetail } from '../../../utils/api';

/* -----------------------------------------------------------------------------
 * Types
 * -------------------------------------------------------------------------- */

export interface FOSDecisionPanelProps {
  /** The decision ID to display, or full decision data */
  decisionId?: string | null;
  /** Pre-loaded decision data (if available, avoids extra fetch) */
  decision?: FOSDecisionDetail | null;
  /** Whether the panel is open (for Sheet mode) */
  isOpen?: boolean;
  /** Close handler (for Sheet mode) */
  onClose?: () => void;
  /** Display mode: 'sheet' slides in from right, 'inline' renders directly */
  mode?: 'sheet' | 'inline';
  /** Additional CSS classes */
  className?: string;
}

/* -----------------------------------------------------------------------------
 * Sub-components
 * -------------------------------------------------------------------------- */

const MetaItem: React.FC<{
  icon: IconType;
  children: React.ReactNode;
  className?: string;
}> = ({ icon, children, className }) => (
  <span className={cn("inline-flex items-center gap-1.5 text-xs text-muted-foreground", className)}>
    <IconWrapper icon={icon} className="h-3 w-3 text-muted-foreground/70" />
    <span className="text-foreground/80">{children}</span>
  </span>
);

const SectionHeader: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <h4 className={cn("text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2", className)}>
    {children}
  </h4>
);

const KeyFact: React.FC<{
  index: number;
  children: React.ReactNode;
}> = ({ index, children }) => (
  <li className="flex items-start gap-2.5">
    <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-muted/80 text-[10px] font-semibold text-muted-foreground mt-0.5">
      {index}
    </span>
    <span className="text-sm text-foreground/85 leading-relaxed">{children}</span>
  </li>
);

const CompensationBreakdownItem: React.FC<{
  label: string;
  amount: number;
}> = ({ label, amount }) => (
  <div className="flex justify-between items-center py-1.5">
    <span className="text-sm text-foreground/70">{label}</span>
    <span className="text-sm font-medium text-foreground tabular-nums">
      £{amount.toLocaleString()}
    </span>
  </div>
);

const LoadingSkeleton: React.FC = () => (
  <div className="p-5 space-y-5">
    <div className="flex items-center gap-3">
      <Skeleton className="h-6 w-28" />
      <Skeleton className="h-6 w-20 rounded-full" />
    </div>
    <div className="flex gap-4">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-4 w-20" />
    </div>
    <Skeleton className="h-16 w-full rounded-lg" />
    <div className="space-y-3">
      <Skeleton className="h-4 w-16" />
      <Skeleton className="h-20 w-full rounded-lg" />
    </div>
    <div className="space-y-3">
      <Skeleton className="h-4 w-16" />
      <Skeleton className="h-32 w-full rounded-lg" />
    </div>
  </div>
);

const ErrorState: React.FC<{ message?: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-16 px-6">
    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10 mb-3">
      <IconWrapper icon={FiAlertTriangle} className="h-5 w-5 text-destructive/60" />
    </div>
    <p className="text-sm font-medium text-destructive">Failed to load decision</p>
    {message && (
      <p className="text-xs text-muted-foreground mt-1 text-center">{message}</p>
    )}
  </div>
);

/* -----------------------------------------------------------------------------
 * PDF Viewer Component
 * Note: FOS website sets X-Frame-Options headers that prevent embedding.
 * We show a helpful message with a direct link instead of a broken iframe.
 * -------------------------------------------------------------------------- */

const PDFViewer: React.FC<{ url: string }> = ({ url }) => (
  <div className="w-full h-full min-h-[400px] bg-muted/20 rounded-lg flex flex-col items-center justify-center p-8 text-center">
    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted/60 mb-4">
      <IconWrapper icon={FiFile} className="h-7 w-7 text-muted-foreground" />
    </div>
    <h4 className="text-sm font-medium text-foreground mb-2">
      PDF Preview Currently Unavailable
    </h4>
    <p className="text-xs text-muted-foreground mb-6 max-w-[280px] leading-relaxed">
      The Financial Ombudsman Service website doesn't allow embedded viewing.
      Open the PDF directly to read the full decision.
    </p>
    <Button variant="default" size="sm" asChild className="gap-2">
      <a href={url} target="_blank" rel="noopener noreferrer">
        <IconWrapper icon={FiExternalLink} className="h-4 w-4" />
        Open Original PDF
      </a>
    </Button>
  </div>
);

/* -----------------------------------------------------------------------------
 * Decision Content Component
 * -------------------------------------------------------------------------- */

interface DecisionContentProps {
  decision: FOSDecisionDetail;
  showPdfTab?: boolean;
}

const DecisionContent: React.FC<DecisionContentProps> = ({ decision, showPdfTab = true }) => {
  const [activeTab, setActiveTab] = useState<'summary' | 'pdf'>('summary');
  const outcomeNormalized = normalizeOutcome(decision.outcome);
  const hasCompensation = !!(decision.redress?.total_compensation && decision.redress.total_compensation > 0);
  const hasPdf = !!decision.pdf_url;

  return (
    <div className="flex flex-col h-full">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 p-5 pb-4">
        {/* Reference and Badge */}
        <div className="flex items-center gap-3 mb-3">
          <span className="font-mono text-base font-semibold text-foreground">
            {decision.reference}
          </span>
          <OutcomeBadge outcome={outcomeNormalized} size="sm" />
        </div>

        {/* Meta Row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {decision.company && (
            <MetaItem icon={FiBriefcase}>{decision.company}</MetaItem>
          )}
          {decision.product_category && (
            <MetaItem icon={FiFileText}>{decision.product_category}</MetaItem>
          )}
          {decision.decision_date && (
            <MetaItem icon={FiCalendar}>
              {new Date(decision.decision_date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </MetaItem>
          )}
        </div>
      </div>

      {/* Tab Navigation (if PDF available) */}
      {showPdfTab && hasPdf ? (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'summary' | 'pdf')} className="flex-1 flex flex-col min-h-0">
          <div className="flex-shrink-0 px-5">
            <TabsList className="w-full grid grid-cols-2 h-9">
              <TabsTrigger value="summary" className="text-xs gap-1.5">
                <IconWrapper icon={FiMessageSquare} className="h-3.5 w-3.5" />
                Summary
              </TabsTrigger>
              <TabsTrigger value="pdf" className="text-xs gap-1.5">
                <IconWrapper icon={FiFile} className="h-3.5 w-3.5" />
                Original PDF
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="summary" className="flex-1 mt-0 overflow-hidden">
            <ScrollArea className="h-full">
              <SummaryContent decision={decision} hasCompensation={hasCompensation} />
            </ScrollArea>
          </TabsContent>

          <TabsContent value="pdf" className="flex-1 mt-0 p-5 pt-4 overflow-hidden">
            <PDFViewer url={decision.pdf_url!} />
          </TabsContent>
        </Tabs>
      ) : (
        <ScrollArea className="flex-1">
          <SummaryContent decision={decision} hasCompensation={hasCompensation} />
        </ScrollArea>
      )}

      {/* Footer with external PDF link */}
      {hasPdf && (
        <div className="flex-shrink-0 p-5 pt-3 border-t border-border/40">
          <Button variant="outline" size="sm" asChild className="w-full gap-2 text-xs">
            <a href={decision.pdf_url!} target="_blank" rel="noopener noreferrer">
              <IconWrapper icon={FiExternalLink} className="h-3.5 w-3.5" />
              Open PDF in New Tab
            </a>
          </Button>
        </div>
      )}
    </div>
  );
};

/* -----------------------------------------------------------------------------
 * Summary Content Component
 * -------------------------------------------------------------------------- */

interface SummaryContentProps {
  decision: FOSDecisionDetail;
  hasCompensation: boolean;
}

const SummaryContent: React.FC<SummaryContentProps> = ({ decision, hasCompensation }) => (
  <div className="p-5 pt-4 space-y-5">
    {/* Core Issue - Prominent */}
    {decision.core_issue && (
      <div className="bg-muted/50 rounded-lg p-4">
        <p className="text-sm text-foreground leading-relaxed">
          {decision.core_issue}
        </p>
      </div>
    )}

    {/* Compensation Card */}
    {hasCompensation && (
      <div className="bg-emerald-50/60 border border-emerald-200/40 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center justify-center w-6 h-6 rounded-md bg-emerald-100">
            <IconWrapper icon={FiDollarSign} className="h-3.5 w-3.5 text-emerald-600" />
          </div>
          <span className="text-xs font-medium text-emerald-700 uppercase tracking-wider">
            Compensation
          </span>
        </div>
        <div className="text-2xl font-bold text-emerald-700 mb-3">
          £{decision.redress!.total_compensation!.toLocaleString()}
        </div>

        {/* Breakdown */}
        <div className="border-t border-emerald-200/40 pt-2">
          {decision.redress?.distress_inconvenience && (
            <CompensationBreakdownItem
              label="Distress & Inconvenience"
              amount={decision.redress.distress_inconvenience}
            />
          )}
          {decision.redress?.financial_loss && (
            <CompensationBreakdownItem
              label="Financial Loss"
              amount={decision.redress.financial_loss}
            />
          )}
          {decision.redress?.interest && (
            <CompensationBreakdownItem
              label="Interest"
              amount={decision.redress.interest}
            />
          )}
        </div>

        {/* Non-monetary remedies */}
        {decision.redress?.non_monetary && decision.redress.non_monetary.length > 0 && (
          <div className="mt-3 pt-3 border-t border-emerald-200/40">
            <span className="text-[10px] font-medium text-emerald-600/80 uppercase tracking-wider">
              Non-monetary Remedies
            </span>
            <ul className="mt-2 space-y-1.5">
              {decision.redress.non_monetary.map((remedy, i) => (
                <li key={i} className="text-xs text-emerald-700/80 flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  <span>{remedy}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )}

    {/* Background */}
    {decision.background && (
      <div>
        <SectionHeader>Background</SectionHeader>
        <p className="text-sm text-foreground/85 leading-relaxed">
          {decision.background}
        </p>
      </div>
    )}

    {/* Key Facts */}
    {decision.key_facts && decision.key_facts.length > 0 && (
      <div>
        <SectionHeader>Key Facts</SectionHeader>
        <ul className="space-y-2.5">
          {decision.key_facts.map((fact, i) => (
            <KeyFact key={i} index={i + 1}>{fact}</KeyFact>
          ))}
        </ul>
      </div>
    )}

    {/* Positions - Compact */}
    {(decision.customer_position || decision.company_position) && (
      <div className="grid gap-4">
        {decision.customer_position && (
          <div>
            <SectionHeader>
              <span className="flex items-center gap-1.5">
                <IconWrapper icon={FiUser} className="h-3 w-3" />
                Customer's Position
              </span>
            </SectionHeader>
            <p className="text-sm text-foreground/85 leading-relaxed">
              {decision.customer_position}
            </p>
          </div>
        )}
        {decision.company_position && (
          <div>
            <SectionHeader>
              <span className="flex items-center gap-1.5">
                <IconWrapper icon={FiBriefcase} className="h-3 w-3" />
                Company's Position
              </span>
            </SectionHeader>
            <p className="text-sm text-foreground/85 leading-relaxed">
              {decision.company_position}
            </p>
          </div>
        )}
      </div>
    )}

    {/* Reasoning - Highlighted */}
    {decision.reasoning_summary && (
      <div className="bg-primary/5 border border-primary/10 rounded-lg p-4">
        <SectionHeader className="text-primary/80">Ombudsman's Reasoning</SectionHeader>
        <p className="text-sm text-foreground/85 leading-relaxed">
          {decision.reasoning_summary}
        </p>
      </div>
    )}
  </div>
);

/* -----------------------------------------------------------------------------
 * Main Component
 * -------------------------------------------------------------------------- */

export const FOSDecisionPanel: React.FC<FOSDecisionPanelProps> = ({
  decisionId,
  decision: preloadedDecision,
  isOpen = true,
  onClose,
  mode = 'sheet',
  className,
}) => {
  // Fetch decision if ID provided and no preloaded data
  const shouldFetch = !!decisionId && !preloadedDecision;
  const { data: fetchedDecision, isLoading, error } = useFOSDecision(
    shouldFetch ? decisionId : null
  );

  const decision = preloadedDecision || fetchedDecision;

  // Sheet mode - renders as a slide-in panel
  if (mode === 'sheet') {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
        <SheetContent
          side="right"
          className={cn(
            "w-full sm:max-w-xl p-0 flex flex-col",
            className
          )}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>FOS Decision Details</SheetTitle>
          </SheetHeader>

          {isLoading ? (
            <LoadingSkeleton />
          ) : error ? (
            <ErrorState message={error.message} />
          ) : decision ? (
            <DecisionContent decision={decision} />
          ) : null}
        </SheetContent>
      </Sheet>
    );
  }

  // Inline mode - renders directly in parent container
  return (
    <div className={cn("bg-card rounded-xl border border-border/60 overflow-hidden", className)}>
      {isLoading ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorState message={error.message} />
      ) : decision ? (
        <DecisionContent decision={decision} showPdfTab={true} />
      ) : null}
    </div>
  );
};

export default FOSDecisionPanel;
